/**
 * @fileoverview Recoveries section workspace.
 * URL: /dashboard/jobs/:jobId/recoveries/:section
 *
 * Dispatches on the section slug from `recoveriesRegistry`.
 */

import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import type { Id } from "@cvx/_generated/dataModel";
import { RECOVERY_SECTIONS_BY_SLUG } from "@/components/jobs/phases/recoveries/recoveriesRegistry";
import { RecoveriesView } from "@/components/jobs/phases/recoveries/RecoveriesView";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/recoveries/$section",
)({
  // `claimId` deep-links straight to a POD claim — used when jumping here
  // from a creditor's Proof of Debt section.
  validateSearch: (search: Record<string, unknown>): { claimId?: string } => ({
    claimId: typeof search.claimId === "string" ? search.claimId : undefined,
  }),
  beforeLoad: ({ params }) => {
    if (!RECOVERY_SECTIONS_BY_SLUG.has(params.section)) {
      throw notFound();
    }
  },
  component: RecoveriesSectionPage,
});

function RecoveriesSectionPage() {
  const { jobId, section } = Route.useParams();
  const { claimId } = Route.useSearch();
  const navigate = useNavigate();
  const meta = RECOVERY_SECTIONS_BY_SLUG.get(section);

  if (!meta) return null;

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="flex min-h-full flex-1 flex-col p-6">
        <RecoveriesView
          jobId={jobId as Id<"jobs">}
          initialClaimId={claimId as Id<"podClaims"> | undefined}
          onClaimClose={() =>
            navigate({
              search: (prev: { claimId?: string }) => ({
                ...prev,
                claimId: undefined,
              }),
              replace: true,
            })
          }
        />
      </div>
    </div>
  );
}
